import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faMoneyBillTrendUp } from '@fortawesome/pro-regular-svg-icons';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { CreditAccount, Payment, UserType } from '@enrollhere/classes';
import { LoadingComponent } from '../../../shared/components/ui-components/loading/loading.component';
import { PayoutsTableComponent } from '../../../shared/components/payouts/payouts-table/payouts-table.component';
import { AuthService } from '../../../shared/services/auth.service';
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental';
import { FilterContainerComponent } from '../../../common/components/filters/signals/filter-container/filter-container.component';
import { PayoutFilter } from '../../../shared/filters/payout.filter';
import { findScrollParent } from '../../../shared/misc/findScrollParent';

@Component({
  selector: 'app-payouts-overview',
  standalone: true,
  imports: [
    FilterContainerComponent,
    FaIconComponent,
    InfiniteScrollDirective,
    LoadingComponent,
    PayoutsTableComponent,
  ],
  providers: [PayoutFilter],
  templateUrl: './payouts-overview.component.html',
  styleUrl: './payouts-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PayoutsOverviewComponent {
  private authService = inject(AuthService);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly filter = inject(PayoutFilter);

  // The app shell (or a modal) scrolls an inner container, not the window;
  // null until rendered, which leaves infinite scroll on the window.
  scrollContainer = signal<HTMLElement | null>(null);

  faMoneyBillTrendUp = faMoneyBillTrendUp;

  // inputs
  hideHeader = input(false);
  filterUserId = input<string>(); // Hidden filter by user.id (owner) - no UI chip shown
  accounts = input<CreditAccount[]>(); // Filter by transaction.account.id

  currentUser = toSignal(this.authService.currentUser$);

  // Computed signal for PayoutsTableComponent data
  tableData = computed(() => ({
    loading: this.loading(),
    payouts: this.payouts().length > 0 ? this.payouts() : null,
    payout: null,
    error: null,
  }));

  payoutsQuery = injectInfiniteQuery(() => ({
    queryKey: this.filter.resultsQueryKey(),
    queryFn: ({ pageParam = '' }: { pageParam: string }) =>
      this.filter.getResults(pageParam),
    getNextPageParam: (
      lastPage: Awaited<ReturnType<typeof this.filter.getResults>>
    ) => lastPage.nextPageToken,
    initialPageParam: '',
    enabled: this.filter.resultsEnabled(),
  }));

  payouts = computed(() => {
    const pages = this.payoutsQuery.data()?.pages;
    if (!pages) return [];
    return pages.flatMap(
      (page: Awaited<ReturnType<typeof this.filter.getResults>>) => page.results
    ) as Payment[];
  });

  loading = computed(
    () => this.payoutsQuery.isLoading() || this.payoutsQuery.isFetching()
  );

  loadingNext = computed(() => this.payoutsQuery.isFetchingNextPage());

  // Current user ID signal for the table
  currentUserId = computed(() => this.currentUser()?.id ?? null);

  constructor() {
    afterNextRender(() => {
      this.scrollContainer.set(findScrollParent(this.elementRef.nativeElement));
    });

    // Effect to mark filter as initialized when user is available
    effect(
      () => {
        const user = this.currentUser();
        if (user?.userType && user?.id) {
          this.filter.userType.set(user.userType);
          if (!this.filter.initialized()) {
            if (user.userType === UserType.AGENT && !this.filterUserId()) {
              this.filter.values.ownerIds.set([user.id]);
            }
            this.filter.initialized.set(true);
          }
        }
      },
      { allowSignalWrites: true }
    );

    // Effect to initialize filter from filterUserId input (hidden owner filter)
    effect(
      () => {
        const filterUserId = this.filterUserId();
        if (filterUserId) {
          this.filter.values.ownerIds.set([filterUserId]);
        }
      },
      { allowSignalWrites: true }
    );

    // Effect to initialize filter from accounts input (credit accounts to filter by)
    effect(
      () => {
        const accounts = this.accounts();
        if (accounts?.length) {
          this.filter.values.transactionAccountIds.set(
            accounts.map(a => ({ id: a.id!, name: a.name }))
          );
        }
      },
      { allowSignalWrites: true }
    );
  }

  onScroll() {
    if (
      this.payoutsQuery.hasNextPage() &&
      !this.payoutsQuery.isFetchingNextPage()
    ) {
      this.payoutsQuery.fetchNextPage();
    }
  }
}
